import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { AuthService } from '../services/auth/auth.service';
import { RefreshTokenService } from '../services/auth/refresh-token.service';
import { endpoints } from '@environment';
import { UserSession } from '@core';
import { Router } from '@angular/router';

const isRefreshing = new BehaviorSubject<boolean>(false);
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

export function authInterceptor(request: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> {
  const refreshTokenService = inject(RefreshTokenService);
  const authService = inject(AuthService);
  const router = inject(Router);

  const initialUrl = request.url;

  let currentUserSession = authService.getSession();
  if (currentUserSession) {
    currentUserSession.refreshToken = refreshTokenService.get();
    request = addHeaders(request, currentUserSession);
  }

  return next(request).pipe(
    catchError((err: HttpErrorResponse) => {
      if (err instanceof HttpErrorResponse && err.status === 401) {
        // If refresh endpoint itself returned 401, clear session and redirect to login
        if (request.url.indexOf(endpoints.auth.v1.authRefreshToken) >= 0) {
          authService.removeSession();
          refreshTokenService.remove();

          router.navigate(['/auth/login'], { queryParams: { returnUrl: initialUrl } });

          return throwError(() => err);
        }

        return handle401Error(request, next, currentUserSession, authService, refreshTokenService, router);
      } else {
        return throwError(() => err);
      }
    })
  );
}

function handle401Error(
  request: HttpRequest<any>,
  next: HttpHandlerFn,
  currentUserSession: UserSession | undefined,
  authService: AuthService,
  refreshTokenService: RefreshTokenService,
  router: Router
): Observable<HttpEvent<any>> {
  currentUserSession = currentUserSession ?? ({} as UserSession);
  currentUserSession.refreshToken = refreshTokenService.get();

  if (!isRefreshing.value) {
    isRefreshing.next(true);
    refreshTokenSubject.next(null);

    const payload = { ...(currentUserSession as any) } as UserSession;

    return authService.refreshToken(payload).pipe(
      switchMap((userSession: UserSession) => {
        isRefreshing.next(false);

        
        if (userSession.refreshToken) {
          refreshTokenService.set(userSession.refreshToken, userSession.expirationDate);
        }

        const sessionToStore: any = { ...userSession };
        delete sessionToStore.refreshToken;
        authService.setSession(sessionToStore);

        refreshTokenSubject.next(userSession.token ?? null);

        // if this was a logout call and server expects refresh token in body, attach it
        if (request.url.indexOf(endpoints.auth.v1.authLogout) >= 0 && request.body) {
          return next(addHeaders(handleLogout(request, userSession.refreshToken!), sessionToStore));
        }

        return next(addHeaders(request, sessionToStore));
      }),
      catchError((refreshErr) => {
        isRefreshing.next(false);
        authService.removeSession();
        refreshTokenService.remove();
        router.navigate(['/auth/login'], { queryParams: { returnUrl: request.url } });
        return throwError(() => refreshErr);
      })
    );
  } else {
    return refreshTokenSubject.pipe(
      filter((token) => token != null),
      take(1),
      switchMap((jwt) => {
        if (currentUserSession) currentUserSession.token = jwt!;
        return next(addHeaders(request, currentUserSession!));
      })
    );
  }
}

function handleLogout(request: HttpRequest<any>, refreshToken: string) {
  const logoutUserSession = (request.body ?? {}) as any;
  logoutUserSession.refreshToken = refreshToken;
  return request.clone({ body: logoutUserSession });
}

function addHeaders(request: HttpRequest<any>, userSession: UserSession | any): HttpRequest<any> {
  if (request.url.indexOf(endpoints.auth.v1.authRefreshToken) >= 0) {
    return request.clone();
  }

  if (userSession && userSession.token) {
    return request.clone({ setHeaders: { Authorization: `Bearer ${userSession.token}` } });
  }

  return request.clone();
}
